import Popper from 'popper.js';
import { HTMLCustomElement, dateUtils } from '@emartech/ui-framework-utils';
import CustomEvent from 'custom-event';
import JSLogger from '../../../utils/jslogger';
const log = new JSLogger('decommission');

class EDatepicker extends HTMLCustomElement {
  init() {
    this._value = null;
    this._popper = null;
    this._disabled = false;
    this._inline = false;
    this._clearButtonVisible = true;

    this._dom = {};
    this._dom.calendar = this._createCalendarElement(this.value);
    this._dom.popper = this._createPopperElement();
    this._dom.arrow = this._createArrowElement();
    this._dom.popper.appendChild(this._dom.arrow);
    this._dom.popper.appendChild(this._dom.calendar);

    this._events = {};
    this._events.update = this._updateDate.bind(this);
    this._events.keyup = this._keyupEvent.bind(this);
    this._events.closeOnOutsideClick = this._closeOnOutsideClick.bind(this);

    this._build();
  }

  connectedCallback() {
    super._cleanupContainer('.e-datepicker-new');
    this.appendChild(this._dom.picker);
    log.log('EDatepicker');
  }

  disconnectedCallback() {
    this.close();
  }

  static get observedAttributes() {
    return ['value', 'disabled', 'inline', 'invalid', 'format', 'no-clear', 'startdate', 'enddate'];
  }

  set value(value) {
    if (this.value === value) {
      return;
    }

    this._value = value;
    this.setAttribute('value', this.value);
    this._dom.calendar.setAttribute('date', this.value);
    this._dom.input.datetime = this.value;
    this._handleClearButton();
    this._dispatchEvent('update');
  }

  get value() {
    return this._value;
  }

  set disabled(value) {
    this._disabled = this._convertAttributeToBoolean(value);
    const classListHandler = this.disabled ? 'add' : 'remove';
    this._dom.picker.classList[classListHandler]('e-input-disabled');
  }

  get disabled() {
    return this._disabled;
  }

  set invalid(value) {
    this._invalid = this._convertAttributeToBoolean(value);
    const classListHandler = this.invalid ? 'add' : 'remove';
    this._dom.picker.classList[classListHandler]('e-input-error');
  }

  get invalid() {
    return this._invalid;
  }

  set inline(value) {
    this._inline = this._convertAttributeToBoolean(value);
    const classListHandler = this._inline ? 'add' : 'remove';
    this._dom.picker.classList[classListHandler]('e-datepicker-new-inline');
  }

  get inline() {
    return this._inline;
  }

  set format(value) {
    log.warn('datepicker set format!');
  }

  set noClear(value) {
    this._clearButtonVisible = !super._convertAttributeToBoolean(value);
    this._handleClearButton();
  }

  set startdate(value) {
    this._dom.calendar.setAttribute('startdate', value);
  }

  set enddate(value) {
    this._dom.calendar.setAttribute('enddate', value);
  }

  open() {
    if (this._popper || this.disabled) {
      return;
    }

    document.addEventListener('keyup', this._events.keyup);
    document.body.addEventListener('mouseup', this._events.closeOnOutsideClick);

    this._dom.closer.classList.remove('e-hidden');

    this._popper = this._initPopper(this._dom.picker, this._dom.popper);

    this._dom.calendar.addEventListener('update', this._events.update);
    this._dom.calendar.addEventListener('render', this._popper.update);
  }

  close() {
    document.removeEventListener('keyup', this._events.keyup);
    document.body.removeEventListener('mouseup', this._events.closeOnOutsideClick);

    this._dom.closer.classList.add('e-hidden');
    this._dom.picker.blur();
    this._dispatchEvent('close');

    this._destroyPopper();
  }

  clear() {
    this._value = '';
    this._dom.input.datetime = '';
    this._handleClearButton();
    this._dispatchEvent('update');
  }

  _build() {
    this._dom.clear = this._createClearButtonElement();
    this._dom.picker = this._createPickerElement();
    this._dom.input = this._createValueElement();
    this._dom.closer = this._createCloserElement();

    this._dom.picker.appendChild(this._dom.closer);
    this._dom.picker.appendChild(this._dom.input);
    this._dom.closer.addEventListener('click', this.close.bind(this));
    this._dom.clear.addEventListener('click', this.clear.bind(this));
  }

  _createPopperElement() {
    const element = document.createElement('div');
    element.className = 'e-popover';
    element.tabIndex = -1;
    return element;
  }

  _createCalendarElement(date) {
    const element = document.createElement('e-calendar');
    element.setAttribute('tabindex', -1);
    element.setAttribute('date', date);
    element.setAttribute('sticky', true);
    return element;
  }

  _createPickerElement() {
    const element = document.createElement('div');
    element.className = 'e-datepicker-new e-input';
    element.setAttribute('tabindex', 0);
    element.addEventListener('focus', this.open.bind(this));
    element.addEventListener('blur', this._blur.bind(this));

    const icon = document.createElement('e-icon');
    icon.setAttribute('icon', 'calendar-o');
    element.appendChild(icon);
    return element;
  }

  _createCloserElement() {
    const element = document.createElement('div');
    element.className = 'e-datepicker-new__close e-hidden';
    return element;
  }

  _createValueElement() {
    const element = document.createElement('e-time');
    element.format = 'date';
    element.className = 'e-datepicker-new__value';
    return element;
  }

  _createClearButtonElement() {
    const element = document.createElement('button');
    element.type = 'button';
    element.className = 'e-datepicker-new__clear';
    return element;
  }

  _createArrowElement() {
    const element = document.createElement('div');
    element.className = 'e-popover__arrow';
    return element;
  }

  _handleClearButton() {
    const isValueEmpty = this.value === null || this.value === 'null' || this.value === '';
    const isClearVisible = this._dom.clear.parentNode;

    if (isClearVisible && !this._clearButtonVisible) {
      this._dom.picker.removeChild(this._dom.clear);
    }

    if (!this._clearButtonVisible || isValueEmpty && !isClearVisible) {
      return;
    }

    const actionHandler = isValueEmpty ? 'removeChild' : 'appendChild';
    this._dom.picker[actionHandler](this._dom.clear);
  }

  _updateDate(event) {
    this.value = event.detail.date;
  }

  _dispatchEvent(name) {
    this.dispatchEvent(new CustomEvent(name, {
      'detail': {
        'date': this.value,
        'dateObj': dateUtils.parse(this.value)
      }
    }));
  }

  _keyupEvent(event) {
    if (event.keyCode === 27) {
      this.close();
    }
  }

  _blur(event) {
    if (this._disabled) {
      return;
    }

    const relatedTarget = event.relatedTarget || event.explicitOriginalTarget || document.activeElement;

    if (relatedTarget !== this._dom.popper && !this._dom.popper.contains(relatedTarget)) {
      this.close();
    }
  }

  _closeOnOutsideClick(event) {
    if (event && event.target && (this._dom.popper.contains(event.target) || this._dom.picker.contains(event.target))) {
      if (this._dom.calendar.contains(event.target)) {
        this._dom.picker.focus();
      }
      return;
    }

    this.close();
  }

  _initPopper(pickerElement, popperElement) {
    document.body.appendChild(popperElement);

    return new Popper(pickerElement, popperElement, {
      placement: 'bottom',
      removeOnDestroy: true,
      modifiers: {
        arrow: {
          element: this._dom.arrow
        }
      }
    });
  }

  _destroyPopper() {
    if (!this._popper) {
      return;
    }

    this._popper.destroy();
    this._popper = null;
  }
}

export default EDatepicker;
